import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Rocket, Package } from 'lucide-react';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useContainersStore } from '@/deploy/stores/containers.store';
import { promotionService, type PromotionJobSnapshot } from '@/deploy/services/promotion.service';
import { containerReleasesService, type ContainerRelease, type ReleaseStatus } from '@/deploy/services/container-releases.service';
import styles from './DeployActivityBadge.module.scss';

// ============================================================
// Petit indicateur dans la navbar du haut (espace Déploiement) : apparaît UNIQUEMENT
// quand une promotion et/ou une release est en cours, montre l'avancement et renvoie
// vers la page de suivi correspondante. Poll adaptatif (rapide si actif, lent au repos).
// ============================================================

const POLL_ACTIVE_MS = 4_000;
const POLL_IDLE_MS = 20_000;

function isActiveRelease(s: ReleaseStatus): boolean {
  return !['released', 'cancelled', 'failed', 'update-failed'].includes(s);
}

export function DeployActivityBadge() {
  const navigate = useNavigate();
  const projectId = useSessionStore((s) => s.currentProject().id);

  const [promos, setPromos] = useState<PromotionJobSnapshot[]>([]);
  const [activeReleases, setActiveReleases] = useState<ContainerRelease[]>([]);

  // Jeton de projet : une écriture d'un refresh démarré pour un ancien projet est ignorée
  // (évite un flash d'activité du mauvais projet après un switch).
  const projectRef = useRef(projectId);
  projectRef.current = projectId;

  // Reset immédiat au changement de projet.
  useEffect(() => { setPromos([]); setActiveReleases([]); }, [projectId]);

  // Renvoie true si une activité est en cours (utilisé pour choisir la cadence de poll).
  const refresh = useCallback(async (): Promise<boolean> => {
    const pid = projectId;
    if (!pid) return false;
    let anyActive = false;
    // Promotions : 1 appel léger.
    try {
      const jobs = await promotionService.listJobs(pid);
      if (projectRef.current !== pid) return anyActive; // projet changé entre-temps → on n'écrit pas
      setPromos(jobs);
      if (jobs.some((j) => !j.done)) anyActive = true;
    } catch { /* best-effort : on garde l'état précédent */ }
    // Releases : s'assure que les containers sont chargés (retry à chaque tick si échec), puis active() par container.
    try {
      const store = useContainersStore.getState();
      if (store.loadedProjectId !== pid) {
        try { await store.load(pid); } catch { /* retry au prochain tick */ }
      }
      if (projectRef.current !== pid) return anyActive;
      const pcs = useContainersStore.getState().containers.filter((c) => c.projectId === pid);
      const results = await Promise.all(
        pcs.map((c) => containerReleasesService.active(pid, c.code).catch(() => null)),
      );
      if (projectRef.current !== pid) return anyActive;
      const active = results.filter((r): r is ContainerRelease => !!r && isActiveRelease(r.status));
      setActiveReleases(active);
      if (active.length) anyActive = true;
    } catch { /* best-effort */ }
    return anyActive;
  }, [projectId]);

  // Poll adaptatif : la cadence suit l'activité FRAÎCHE renvoyée par refresh (pas un flag périmé).
  useEffect(() => {
    let alive = true;
    let t: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const active = await refresh();
      if (!alive) return;
      t = setTimeout(() => void tick(), active ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    t = setTimeout(() => void tick(), 500);
    return () => { alive = false; if (t) clearTimeout(t); };
  }, [refresh]);

  const activePromos = useMemo(() => promos.filter((p) => !p.done), [promos]);
  const promoCount = activePromos.length;
  const relCount = activeReleases.length;
  if (promoCount === 0 && relCount === 0) return null;

  // Promotion la plus récente (listJobs trié récent → ancien) pour l'avancement.
  const promo = activePromos[0];
  const promoTotal = promo ? (promo.totalSteps || promo.results.length) : 0;
  const promoStep = promo && promoTotal ? Math.min(Math.max(promo.currentStepIndex + 1, 1), promoTotal) : 0;

  return (
    <div className={styles.wrap}>
      {promoCount > 0 && (
        <button
          type="button"
          className={styles.chip}
          data-kind="promo"
          onClick={() => navigate('/deploiement/deployments')}
          title="Voir le suivi des déploiements"
        >
          <Loader2 size={13} strokeWidth={2.4} className={styles.spin} />
          <Rocket size={12} strokeWidth={2.2} />
          <span className={styles.label}>
            {promoCount > 1 ? `${promoCount} déploiements` : (promoTotal ? `Déploiement · ${promoStep}/${promoTotal}` : 'Déploiement…')}
          </span>
        </button>
      )}
      {relCount > 0 && (
        <button
          type="button"
          className={styles.chip}
          data-kind="release"
          onClick={() => navigate('/deploiement/deploy/progress')}
          title="Voir le suivi des releases"
        >
          <Loader2 size={13} strokeWidth={2.4} className={styles.spin} />
          <Package size={12} strokeWidth={2.2} />
          <span className={styles.label}>{relCount > 1 ? `${relCount} releases` : '1 release'}</span>
        </button>
      )}
    </div>
  );
}
